import React from "react";

import { IFieldType, IFormFields, IFormWidgetType } from "shared/types";
import { copy } from "shared/assets";
import { IEquipmentSubcategoryItem, IStage } from "Venues/types";
import { DistanceUnits, WeightUnits } from "shared/utils";
import { equipmentCategoryList } from "Venues/utils";
import { Dimensions, Weight } from "shared/assets/icons";
import { Icon } from "shared/styles";
import { API_ENDPOINTS, FIELD_SIZES } from "shared/constants";

const { errors, equipmentProperties } = copy;

export const getFormData = (
  stages: IStage[],
  subCategories: IEquipmentSubcategoryItem[] | undefined,
  setSubCategories: (subCategories: IEquipmentSubcategoryItem[]) => void,
  hideCategories?: boolean,
  disableStages?: string[]
): IFormFields => [
  {
    type: IFieldType.checkboxGroup,
    name: "stages",
    label: equipmentProperties.stages.title,
    options: stages.map(({ id, title }) => ({
      label: title,
      value: API_ENDPOINTS.STAGE(id),
      disabled: disableStages?.includes(id),
    })),
    validation: {
      required: errors.required,
    },
    style: {
      gridColumn: "1/6",
      mobile: {
        gridColumn: "1/7",
      },
    },
  },
  ...(!hideCategories
    ? ([
        {
          type: IFieldType.select,
          name: "mainCategory",
          label: equipmentProperties.category.title,
          options: equipmentCategoryList?.map(({ category, label }) => ({
            label,
            value: category,
          })),
          placeholder: equipmentProperties.category.placeholder,
          onChange: (item: any) => {
            if (item) {
              const subCategories = equipmentCategoryList?.find(
                ({ category }) => category === item?.value
              )?.categories;
              if (subCategories) {
                setSubCategories(subCategories);
              }
            }
          },
          validation: {
            required: errors.required,
          },
          style: {
            gridColumn: "1/4",
            mobile: {
              gridColumn: "1/7",
            },
          },
        },
        {
          type: IFieldType.select,
          name: "category",
          label: equipmentProperties.subcategory.title,
          placeholder: equipmentProperties.subcategory.placeholder,
          options:
            subCategories?.map(({ category, label }) => ({
              label,
              value: category,
            })) || [],
          disabled: !subCategories,
          validation: {
            required: errors.required,
          },
          style: {
            gridColumn: "4/7",
            mobile: {
              gridColumn: "1/7",
            },
          },
        },
      ] as IFormFields)
    : []),
  {
    type: IFieldType.text,
    name: "brand",
    label: equipmentProperties.brand.title,
    validation: {
      required: errors.required,
    },
    style: {
      gridColumn: "1/3",
      mobile: {
        gridColumn: "1/7",
      },
    },
  },
  {
    type: IFieldType.text,
    name: "model",
    label: equipmentProperties.model.title,
    validation: {
      required: errors.required,
    },
    style: {
      gridColumn: "3/5",
      mobile: {
        gridColumn: "1/7",
      },
    },
  },
  {
    type: IFieldType.number,
    name: "amount",
    label: equipmentProperties.units.title,
    validation: {
      required: errors.required,
      min: 1,
      max: FIELD_SIZES.NUMBER,
    },
    style: {
      gridColumn: "5/7",
      mobile: {
        gridColumn: "1/7",
      },
    },
  },
  {
    name: "unitConverter",
    widget: IFormWidgetType.unitConverter,
    style: {
      gridColumn: "1/7",
      mobile: {
        gridColumn: "1/7",
      },
    },
  },
  {
    label: (
      <React.Fragment>
        <Icon as={Dimensions} key="dimensions_icon" />
        <span key="dimensions_label">
          {equipmentProperties.dimensions.title}
        </span>
      </React.Fragment>
    ),
    name: "dimensions",
    hideContainer: true,
    separator: "✕",
    style: {
      gridTemplateColumns: "1fr 1fr 1fr",
      gridColumn: "1/4",
      mobile: {
        gridColumn: "1/7",
      },
    },
    fields: [
      {
        type: IFieldType.dimension,
        name: "width",
        label: "Width",
        hideLabel: true,
        min: 0,
        max: FIELD_SIZES.NUMBER,
        unit: {
          metric: DistanceUnits.m,
          imperial: DistanceUnits.ft,
        },
      },
      {
        type: IFieldType.dimension,
        name: "height",
        label: "Height",
        hideLabel: true,
        min: 0,
        max: FIELD_SIZES.NUMBER,
        unit: {
          metric: DistanceUnits.m,
          imperial: DistanceUnits.ft,
        },
      },
      {
        type: IFieldType.dimension,
        name: "depth",
        label: "Depth",
        hideLabel: true,
        min: 0,
        max: FIELD_SIZES.NUMBER,
        unit: {
          metric: DistanceUnits.m,
          imperial: DistanceUnits.ft,
        },
      },
    ],
  },
  {
    type: IFieldType.dimension,
    name: "weight",
    label: (
      <React.Fragment>
        <Icon as={Weight} key="weight_icon" />
        <span key="weight_label">{equipmentProperties.weight.title}</span>
      </React.Fragment>
    ),
    min: 0,
    max: FIELD_SIZES.NUMBER,
    isFullWidth: true,
    unit: {
      metric: WeightUnits.kg,
      imperial: WeightUnits.lbs,
    },
    style: {
      gridColumn: "4/7",
      mobile: {
        gridColumn: "1/7",
      },
    },
  },
  {
    type: IFieldType.rte,
    name: "remark",
    label: equipmentProperties.remark.title,
    style: {
      gridColumn: "1/7",
      mobile: {
        gridColumn: "1/7",
      },
    },
  },
];

export const defaultValues = {
  category: "",
  mainCategory: "",
  stages: "",
  amount: "",
};
